import AuthUtils from './AuthUtils';
import store from '../store';
import { registerAuthToken, } from '../actions/auth';

/**
 * Exchange the current access token for a fresh one using the HttpOnly refresh
 * cookie, updating localStorage and the store on success. The current token may
 * already be expired — the backend re-derives the user from it either way.
 *
 * Deliberately free of lock-screen and logout side effects: what a failure means
 * depends on the caller. Mid-session it locks the screen to protect open work; at
 * boot it just falls back to the login form.
 *
 * @returns {Promise<string>} the refreshed access token
 * @throws if the session could not be refreshed
 */
const refreshAccessToken = async () => {
  const { auth = {}, } = store.getState();
  const { authentication = {}, } = auth;
  const { token, tenantUserId, } = authentication;

  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token || '',
    },
    body: JSON.stringify({
      tenantUserId,
      accessToken: token,
      fingerprint: AuthUtils.getFingerprint(authentication),
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed (status ${response.status})`);
  }

  const data = await response.json();

  if (!data.isAuthenticated || !data.token) {
    throw new Error('Token refresh failed');
  }

  const authData = {
    ...data,
    token: data.accessToken || data.token,
    tenantUserId: data.tenantUserId,
    // Preserve the sign-in fingerprint; the refresh response does not carry it.
    fingerprint: AuthUtils.getFingerprint(authentication),
  };

  window.localStorage.setItem('PAYIT_TOKEN', JSON.stringify(authData));
  store.dispatch(registerAuthToken(authData));

  return authData.token;
};

export default refreshAccessToken;